import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  getPhoenixApiTenantUrl,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';

/**
 * Request body for getting a presigned upload URL
 */
export interface GetUploadUrlBody {
  tenant: string;
  fileType: string;
  md5Hash?: string;
}

/**
 * Response type for getUploadUrl - returns plain text URL
 */
export type GetUploadUrlResult = string;

/**
 * Request body for getting multiple receipt download URLs
 */
export interface GetReceiptUrlsBody {
  tenant: string;
  receiptIds: string[];
}

/**
 * Response type for getReceiptUrls - returns map of image ID to presigned URL
 */
export type GetReceiptUrlsResult = Record<string, string>;

export interface ReceiptImageAnalysis {
  receiptNumber: string | null;
  /** ISO date (YYYY-MM-DD) or null. */
  receiptDate: string | null;
  /** Total in cents, or null. */
  total: number | null;
}

export interface IServiceProviderReceiptUploadPhoenixApi extends Api {
  /**
   * Get presigned S3 upload URL for a receipt image
   *
   * @param imageId - UUID of the receipt image
   * @param body - Request body with tenant, fileType, and optional md5Hash
   * @returns Presigned S3 upload URL as plain text
   *
   * @example
   * ```ts
   * const url = await api.getUploadUrl(
   *   '550e8400-e29b-41d4-a716-446655440000',
   *   {
   *     tenant: 'test_tenant',
   *     fileType: 'image/jpeg',
   *     md5Hash: 'abc123=='
   *   }
   * );
   * ```
   */
  getUploadUrl(
    imageId: string,
    body: GetUploadUrlBody
  ): Promise<GetUploadUrlResult>;

  /**
   * Get presigned S3 download URLs for multiple receipt images
   *
   * @param body - Request body with tenant and array of receipt IDs
   * @returns Map of image UUID to presigned download URL
   *
   * @example
   * ```ts
   * const urls = await api.getReceiptUrls({
   *   tenant: 'test_tenant',
   *   receiptIds: [
   *     '550e8400-e29b-41d4-a716-446655440001',
   *     '550e8400-e29b-41d4-a716-446655440002'
   *   ]
   * });
   * // Returns: { "550e8400-...001": "https://...", "550e8400-...002": "https://..." }
   * ```
   */
  getReceiptUrls(body: GetReceiptUrlsBody): Promise<GetReceiptUrlsResult>;

  /**
   * Run receipt image analysis on an already uploaded receipt image.
   *
   * @example
   * ```ts
   * const result = await api.analyzeReceiptImage('550e8400-...', { tenant: 'test_tenant' });
   * // { receiptNumber: 'R-42', receiptDate: '2024-01-15', total: 5564 }
   * ```
   */
  analyzeReceiptImage(
    imageId: string,
    body: { tenant: string }
  ): Promise<ReceiptImageAnalysis>;
}

export class ServiceProviderReceiptUploadPhoenixApi
  implements IServiceProviderReceiptUploadPhoenixApi
{
  baseUrl: string;
  headers: Headers;

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl('service-provider', apiUrl);
    this.headers = new Headers({
      'content-type': 'application/json',
    });
  }

  async getUploadUrl(
    imageId: string,
    body: GetUploadUrlBody
  ): Promise<GetUploadUrlResult> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/receipt-image/${imageId}`,
      {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(body),
      }
    );

    // Check if the response is successful
    if (!result.ok) {
      const errorBody = await result.text();
      throw new Error(
        `Failed to get upload URL: HTTP ${result.status} - ${errorBody}`
      );
    }

    // Phoenix returns plain text URL
    return result.text();
  }

  async getReceiptUrls(
    body: GetReceiptUrlsBody
  ): Promise<GetReceiptUrlsResult> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/receipt-image/multi`,
      {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(body),
      }
    );

    if (result.ok) {
      return result.json() as Promise<GetReceiptUrlsResult>;
    }

    // Return empty map on error
    return {};
  }

  async analyzeReceiptImage(
    imageId: string,
    body: { tenant: string }
  ): Promise<ReceiptImageAnalysis> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/receipt-image/${imageId}/analyze`,
      {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(body),
      }
    );
    if (!result.ok) {
      throw new Error(`Failed to analyze receipt image: HTTP ${result.status}`);
    }
    return result.json() as Promise<ReceiptImageAnalysis>;
  }
}
